import type { GroupConfigurations } from '@/types/group'
import type { CoachingConfiguration, OrganizationConfigurations } from '@/types/organization'

export type ConfigurationContext = 'organization' | 'group'

export type AssessmentsConfiguration = {
  readonly enabled: boolean
  readonly is_mandatory: boolean
  readonly time_between_assessments_days: number | null
  // Days after the needs analysis before a mandatory assessment is required;
  // 0 means immediately. Unset inherits the system default (7).
  readonly mandatory_grace_days?: number | null
}

export type UserConfiguration = {
  readonly default_license_duration_days?: number
  readonly teacher_access?: {
    readonly enabled: boolean
  }
}

export type LiveSessionConfiguration = {
  readonly enabled: boolean
  readonly max_duration_minutes: number | null
  readonly max_participants: number | null
}

export type NeedsAnalysisConfiguration = {
  readonly enabled: boolean
}

export type SkillsDevelopmentConfiguration = {
  readonly writing_ai_tool?: {
    readonly enabled: boolean
  }
  readonly speaking_ai_tool?: {
    readonly enabled: boolean
  }
}

// Union type for all configuration types
export type Configuration =
  | AssessmentsConfiguration
  | UserConfiguration
  | LiveSessionConfiguration
  | NeedsAnalysisConfiguration
  | SkillsDevelopmentConfiguration
  | CoachingConfiguration

// Configuration status information
export type ConfigurationStatus = {
  readonly status: 'configured' | 'available'
  readonly icon: React.ComponentType<{ className?: string }>
  readonly variant: 'default' | 'secondary'
}

// Organization with groups data types
export type GroupConfigurationItem = {
  readonly id: string
  readonly configuration: GroupConfigurations
}

export type OrganizationUpdateFunction = (
  data: Partial<OrganizationConfigurations>
) => Promise<OrganizationConfigurations>

export type GroupUpdateFunction = (groupId: string, data: Partial<GroupConfigurations>) => Promise<GroupConfigurations>

// Feature access paths
export enum FeatureAccessPath {
  LIVE_SESSIONS_GROUP_CLASSES_ENABLED = 'live_sessions.group_classes.enabled',
  LIVE_SESSIONS_PRIVATE_CLASSES_ENABLED = 'live_sessions.private_classes.enabled',
  ASSESSMENTS_ENABLED = 'assessments.enabled',
  ASSESSMENTS_MANDATORY = 'assessments.is_mandatory',
  ASSESSMENTS_MANDATORY_GRACE_DAYS = 'assessments.mandatory_grace_days',
}
